import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { useFilterAndSearchEffect } from "./useFilterAndSearchEffect";
import { useFilterPayload } from "./useFilterPayload";
import { usePaginationReset } from "./usePaginationReset";
import { ReviewPendingOrdersState } from "./useReviewPendingOrdersTableData";
import { useSearchTerm } from "./useSearchTerm";
import { useValidFilters } from "./useValidFilters";

/**
 * Composes filter, search, and pagination hooks for the Review Pending Orders table.
 * Coordinates search term state, valid filters, filter payload generation,
 * and the side-effect that triggers data re-fetching on changes.
 *
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param setTableData - State setter for the transformed table row data
 * @param setReviewPendingOrdersState - State setter for the orders loading/data/error state
 * @param authStatus - Optional auth status segment to scope the payload
 * @returns Filter payload, search term, pagination reset count, valid filters, and their setters
 */
export const useFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
  setReviewPendingOrdersState: React.Dispatch<
    React.SetStateAction<ReviewPendingOrdersState>
  >,
  authStatus?: string,
) => {
  const { searchTerm, setSearchTerm } = useSearchTerm();
  const { paginationResetCount, setPaginationResetCount } =
    usePaginationReset();
  const validFilters = useValidFilters();
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    authStatus,
  );

  useFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setReviewPendingOrdersState,
    setPaginationResetCount,
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    setPaginationResetCount,
    validFilters,
  };
};
